import React, { useMemo, useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController,
  PieController,
  DoughnutController,
  ScatterController,
  RadarController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'hammerjs';
import { Box, IconButton, Tooltip as MuiTooltip, Typography } from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import vsaasTheme from '../theme';
import {
  formatDateTimeDisplay,
  isDayOfWeekKeySet,
  parseDayOfWeekNumber,
  isDayOfMonthKeySet,
  parseDayOfMonthNumber,
  parseTemporalValue,
} from '../utils/dateBucketFormat';
import { resolveThresholdTextColor, resolveThresholdLabel } from '../utils/colorThresholds';
import { formatCalculatedDisplay, isCalculatedColumnKey, normalizeCalculatedColumns } from '../utils/calculatedColumns';
import {
  canFillTimeAxisGaps,
  expandTimeAxisKeys,
  normalizeTimeAxisKey,
  supportsFillTimeAxisGaps,
} from '../utils/timeAxisGaps';
import { formatDurationMinutes } from '../utils/durationFormat';
import {
  resolvePieLegendFontSize,
  normalizePieLabelPosition,
  scalePieLegendFontSize,
  resolvePieLegendPositionForViewport,
  isCompactPieLegendViewport,
  pieLabelsInLegend as isPieLegendLabelMode,
  pieChartDrawMode,
  resolvePieMinChartLabelPercent,
  DEFAULT_PIE_MIN_CHART_LABEL_ANGLE_DEG,
} from '../utils/pieChartOptions';
import {
  barValueLabelFontPx,
  buildLineValueLabelIndexSet,
  isCompactBarLabelViewport,
  MIN_GROUPED_BAR_LABEL_PX,
  fitBarSegmentLabelFontPx,
  resolveBarLabelMode,
  visibleBarSegmentSpan,
  resolveCategoryAxisTickOptions,
  resolveLineValueLabelMode,
  tryDrawLabel,
} from '../utils/barChartLabels';
import HeatmapGridWidget from './HeatmapGridWidget';
import ImageDensityWidget from './ImageDensityWidget';
import ImageCarouselWidget from './ImageCarouselWidget';
import MapWidget from './MapWidget';
import GeoHeatmapWidget from './GeoHeatmapWidget';
import IntervalTimelineChart from './IntervalTimelineChart';
import { useStyleTemplate } from '../contexts/StyleTemplateContext';
import { buildChartZoomPluginOptions, isChartZoomType } from '../utils/chartZoomOptions';
import {
  buildNarrativePlaceholders,
  renderNarrativeSegments,
  slugifyPlaceholderKey,
  isInternalIdColumn,
  dedupePlaceholderKey,
} from '../utils/narrativeTemplate';

function hexToRgba(hex, a = 0.6) {
  if (!hex || !hex.startsWith('#')) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

/** Holgura solo arriba del eje Y (Chart.js 4.4 grace es simétrico; no acepta { top, bottom }). */
function applyYAxisTopHeadroomOnly(scale, fraction) {
  const min = scale.min;
  const max = scale.max;
  if (!Number.isFinite(max)) return;
  const pct = Number(fraction);
  if (!Number.isFinite(pct) || pct <= 0) return;
  const span = Number.isFinite(min) ? max - min : Math.abs(max) || 1;
  scale.max = max + (span > 0 ? span : Math.abs(max) || 1) * pct;
}

function isPureNumericHourKey(value) {
  const s = String(value ?? '').trim();
  if (!/^\d{1,2}$/.test(s)) return false;
  const n = parseInt(s, 10);
  return Number.isFinite(n) && n >= 0 && n <= 23;
}

/** Conserva el orden de aparición en los datos (p. ej. ORDER BY del SQL). */
function axisCategoryKey(value) {
  if (value === null || value === undefined) return '';
  return String(value);
}

const EMPTY_CATEGORY_LABEL = '(sin dato)';

function formatCategoryLabel(value, xDateBucket, xDateDisplayFormat) {
  if (value === null || value === undefined || value === '') return EMPTY_CATEGORY_LABEL;
  const formatted = formatDateTimeDisplay(value, {
    dateDisplayFormat: xDateDisplayFormat,
    dateBucket: xDateBucket,
  });
  if (formatted) return formatted;
  const shouldFormatAsHour =
    xDateBucket === 'hourOfDay' ||
    (xDateBucket !== 'dayOfWeek' && isPureNumericHourKey(value));
  if (shouldFormatAsHour) {
    const n = parseInt(String(value).trim(), 10);
    return String(n).padStart(2, '0');
  }
  return String(value);
}

function rowValue(row, column) {
  if (!row || column == null || column === '') return undefined;
  if (Object.prototype.hasOwnProperty.call(row, column)) return row[column];
  const name = String(column);
  const lower = name.toLowerCase();
  const keys = Object.keys(row);
  const exactCi = keys.find((k) => k.toLowerCase() === lower);
  if (exactCi) return row[exactCi];
  // Alias del SQL builder: columna_tabla (ej. sensor_type_assets) o columna_joinAlias para
  // un self-join / referencia seguida (ej. parent_via_parent_asset_id). Red de seguridad para
  // cuando el alias guardado en el gráfico quedó desactualizado (ej. un join nuevo forzó
  // sufijo donde antes no hacía falta). makeAlias (columnExpr.js) trunca el nombre de tabla a
  // 24 caracteres y el joinAlias a 60: se exige ese límite más amplio acá para no tomar por
  // buena una coincidencia de prefijo con una columna distinta y mucho más larga, que nunca
  // podría venir de ese mecanismo.
  const prefix = `${lower}_`;
  const prefixHits = keys.filter((k) => {
    if (!k.toLowerCase().startsWith(prefix)) return false;
    const suffixLen = k.length - prefix.length;
    return suffixLen > 0 && suffixLen <= 60;
  });
  if (prefixHits.length === 1) return row[prefixHits[0]];
  return undefined;
}

/** Misma etiqueta de eje X que arma chartData para una fila (mismo xKeyFn + formatCategoryLabel),
 * para que un lookup por etiqueta (tooltip, linea de metrica) encuentre la fila correcta
 * incluso con "Incluir horas/fechas sin datos en el eje X" activo (ahi chartData usa
 * normalizeTimeAxisKey en vez del valor crudo). */
function resolveAxisCategoryLabel(rawValue, { type, xDateBucket, xDateDisplayFormat, advancedConfig }) {
  const fillGaps =
    (type === 'line' || type === 'bar') &&
    advancedConfig?.fillTimeAxisGaps === true &&
    supportsFillTimeAxisGaps(xDateBucket);
  const key = fillGaps ? normalizeTimeAxisKey(rawValue, xDateBucket) : axisCategoryKey(rawValue);
  return formatCategoryLabel(key, xDateBucket, xDateDisplayFormat);
}

function hasNonEmptyCell(row, column) {
  const v = rowValue(row, column);
  return v != null && String(v).trim() !== '';
}

/** Etiqueta de serie: fallback (título o eje Y) si la celda de leyenda viene vacía. */
function seriesLabelFromRow(row, labelColumn, fallbackLabel, labelDateBucket, labelDateDisplayFormat) {
  if (!hasNonEmptyCell(row, labelColumn)) return fallbackLabel;
  const raw = rowValue(row, labelColumn);
  if ((labelDateBucket && labelDateBucket !== 'raw') || (labelDateDisplayFormat && labelDateDisplayFormat !== 'auto')) {
    const formatted = formatDateTimeDisplay(raw, {
      dateDisplayFormat: labelDateDisplayFormat,
      dateBucket: labelDateBucket,
    });
    if (formatted) return formatted;
  }
  return String(raw);
}

function isHourLabelSet(labels) {
  return labels.length > 0 && labels.every((l) => isPureNumericHourKey(l));
}

function sortAxisLabels(labels, { hourCycleStart, direction, xDateBucket } = {}) {
  if (xDateBucket === 'dayOfWeek' && isDayOfWeekKeySet(labels)) {
    const sorted = [...labels].sort(
      (a, b) => (parseDayOfWeekNumber(a) ?? 0) - (parseDayOfWeekNumber(b) ?? 0)
    );
    return String(direction || 'asc').toLowerCase() === 'desc' ? sorted.reverse() : sorted;
  }
  if (xDateBucket === 'dayOfMonth' && isDayOfMonthKeySet(labels)) {
    const sorted = [...labels].sort(
      (a, b) => (parseDayOfMonthNumber(a) ?? 0) - (parseDayOfMonthNumber(b) ?? 0)
    );
    return String(direction || 'asc').toLowerCase() === 'desc' ? sorted.reverse() : sorted;
  }
  const asHours =
    xDateBucket === 'hourOfDay' ||
    hourCycleStart != null ||
    xDateBucket !== 'dayOfWeek';
  if (!asHours || !isHourLabelSet(labels)) return labels;
  const cs = hourCycleStart != null && hourCycleStart !== '' ? Number(hourCycleStart) : null;
  const sorted = [...labels].sort((a, b) => {
    const na = parseInt(a, 10);
    const nb = parseInt(b, 10);
    if (cs != null && Number.isFinite(cs)) {
      const ca = na >= cs ? na - cs : na + (24 - cs);
      const cb = nb >= cs ? nb - cs : nb + (24 - cs);
      return ca - cb;
    }
    return na - nb;
  });
  return String(direction || 'asc').toLowerCase() === 'desc' ? sorted.reverse() : sorted;
}

function uniqueValuesInOrder(items, sortOptions) {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const key = axisCategoryKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  if (sortOptions) return sortAxisLabels(out, sortOptions);
  return out;
}

function calculatedPercentDef(advancedConfig, column) {
  if (!isCalculatedColumnKey(column)) return null;
  const def = normalizeCalculatedColumns(advancedConfig?.tableCalculatedColumns).find((item) => item.key === column);
  return def?.percent ? def : null;
}

function formatBarLabelValue(value, decimalPlaces, durationFormat, percentDef) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '');
  if (percentDef?.percent) return formatCalculatedDisplay(n, percentDef);
  if (durationFormat) {
    return formatDurationMinutes(n, durationFormat);
  }
  if (decimalPlaces != null && decimalPlaces !== '' && Number.isFinite(Number(decimalPlaces))) {
    const dp = parseInt(decimalPlaces, 10);
    return n.toLocaleString(undefined, {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    });
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// Plugin: total arriba de la barra y/o subtotal dentro de cada segmento (responsive + anti-solapamiento)
const barValueLabelsPlugin = {
  id: 'barValueLabels',
  afterDatasetsDraw(chart) {
    const opts = chart.options?.plugins?.barValueLabels;
    if (chart.config.type !== 'bar' || (!opts?.showTotal && !opts?.showSegment)) return;

    const containerWidth = opts.containerWidth || chart.width || 0;
    const categoryCount = chart.data.datasets[0]?.data?.length ?? 0;
    const mode = resolveBarLabelMode({
      showTotal: opts.showTotal,
      showSegment: opts.showSegment,
      containerWidth,
      categoryCount,
    });
    if (!mode.showTotal && !mode.showSegment) return;

    const ctx = chart.ctx;
    const datasets = chart.data.datasets;
    const n = categoryCount;
    const fontPx = barValueLabelFontPx(containerWidth);
    const placed = [];
    const formatValue = (v) =>
      formatBarLabelValue(v, opts?.decimalPlaces, opts?.yColumnIsDuration ? opts.durationFormat : null, opts?.percentDef);

    if (mode.showSegment) {
      datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (!meta || meta.type !== 'bar') return;
        meta.data.forEach((element, index) => {
          if (!element || element.hidden) return;
          const value = dataset.data[index];
          if (value == null || value === '' || Number(value) === 0) return;
          const text = formatValue(value);
          const area = chart.chartArea;
          if (element.horizontal) {
            const span = visibleBarSegmentSpan(
              element.x,
              element.base,
              area?.left ?? -Infinity,
              area?.right ?? Infinity
            );
            if (!span) return;
            const segFontPx = fitBarSegmentLabelFontPx(span.size, fontPx);
            if (!segFontPx) return;
            tryDrawLabel(ctx, text, span.center, element.y, {
              align: 'center',
              baseline: 'middle',
              fontPx: segFontPx,
              placed,
            });
          } else {
            const span = visibleBarSegmentSpan(
              element.y,
              element.base,
              area?.top ?? -Infinity,
              area?.bottom ?? Infinity
            );
            if (!span) return;
            const segFontPx = fitBarSegmentLabelFontPx(span.size, fontPx);
            if (!segFontPx) return;
            tryDrawLabel(ctx, text, element.x, span.center, {
              align: 'center',
              baseline: 'middle',
              fontPx: segFontPx,
              placed,
            });
          }
        });
      });
    }

    const stacked = !!chart.options?.scales?.x?.stacked || !!chart.options?.scales?.y?.stacked;
    if (mode.showTotal) {
      if (stacked) {
        for (let index = 0; index < n; index++) {
          let total = 0;
          let extreme = null;
          let center = null;
          let horizontalBar = false;
          for (let di = 0; di < datasets.length; di++) {
            const v = datasets[di].data[index];
            if (v != null && v !== '') total += Number(v) || 0;
            const meta = chart.getDatasetMeta(di);
            if (!meta || meta.type !== 'bar' || !meta.data[index]) continue;
            const el = meta.data[index];
            if (el && !el.hidden) {
              horizontalBar = !!el.horizontal;
              if (horizontalBar) {
                const r = Math.max(el.x, el.base);
                if (extreme === null || r > extreme) extreme = r;
                if (center === null) center = el.y;
              } else {
                const t = Math.min(el.y, el.base);
                if (extreme === null || t < extreme) extreme = t;
                if (center === null) center = el.x;
              }
            }
          }
          if (extreme == null || center == null || total === 0) continue;
          if (horizontalBar) {
            tryDrawLabel(ctx, formatValue(total), extreme + 4, center, {
              align: 'left',
              baseline: 'middle',
              fontPx,
              placed,
            });
          } else {
            tryDrawLabel(ctx, formatValue(total), center, extreme - 4, {
              align: 'center',
              baseline: 'bottom',
              fontPx,
              placed,
            });
          }
        }
      } else {
        datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (!meta || meta.type !== 'bar') return;
          meta.data.forEach((element, index) => {
            if (!element || element.hidden) return;
            const value = dataset.data[index];
            if (value == null || value === '' || Number(value) === 0) return;
            if (element.horizontal) {
              const barWidth = Math.abs(element.x - element.base);
              if (barWidth < MIN_GROUPED_BAR_LABEL_PX) return;
              tryDrawLabel(ctx, formatValue(value), Math.max(element.x, element.base) + 4, element.y, {
                align: 'left',
                baseline: 'middle',
                fontPx,
                placed,
              });
            } else {
              const barHeight = Math.abs(element.y - element.base);
              if (barHeight < MIN_GROUPED_BAR_LABEL_PX) return;
              tryDrawLabel(ctx, formatValue(value), element.x, Math.min(element.y, element.base) - 4, {
                align: 'center',
                baseline: 'bottom',
                fontPx,
                placed,
              });
            }
          });
        });
      }
    }
  },
};

// Plugin: valor numérico encima de cada punto en gráficos de línea
const lineValueLabelsPlugin = {
  id: 'lineValueLabels',
  afterDatasetsDraw(chart) {
    const opts = chart.options?.plugins?.lineValueLabels;
    if (chart.config.type !== 'line' || !opts?.showValue) return;

    const categoryCount = chart.data.datasets[0]?.data?.length ?? 0;
    const datasetCount = chart.data.datasets.length;
    const mode = resolveLineValueLabelMode({
      showValue: opts.showValue,
      containerWidth: opts.containerWidth || chart.width || 0,
      categoryCount,
      datasetCount,
    });
    if (!mode.showValue) return;

    const ctx = chart.ctx;
    const placed = [];
    const formatValue = (v) =>
      formatBarLabelValue(v, opts?.decimalPlaces, opts?.yColumnIsDuration ? opts.durationFormat : null, opts?.percentDef);

    const refMeta = chart.getDatasetMeta(0);
    const visibleIndices = buildLineValueLabelIndexSet(
      refMeta?.data,
      categoryCount,
      mode.minPxGap
    );

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || meta.type !== 'line') return;
      meta.data.forEach((element, index) => {
        if (!element || element.hidden) return;
        if (!visibleIndices.has(index)) return;
        const value = dataset.data[index];
        if (value == null || value === '') return;
        if (mode.skipZero && Number(value) === 0) return;
        const text = formatValue(value);
        tryDrawLabel(ctx, text, element.x, element.y - 4, {
          align: 'center',
          baseline: 'bottom',
          fontPx: mode.fontPx,
          placed,
        });
      });
    });
  },
};

function seriesPointValue(groupedMap, xValue, fillMissingAsZero) {
  if (!Object.prototype.hasOwnProperty.call(groupedMap, xValue)) {
    return fillMissingAsZero ? 0 : null;
  }
  return groupedMap[xValue];
}

/** Opciones de curva para gráficos de línea (Chart.js stepped / tension). */
export const LINE_INTERPOLATION_OPTIONS = [
  { value: 'linear', label: 'Rectas' },
  { value: 'spline', label: 'Suavizada (spline)' },
  { value: 'step-before', label: 'Escalón — inicio' },
  { value: 'step-middle', label: 'Escalón — medio' },
  { value: 'step-after', label: 'Escalón — fin' },
];

function resolveLineCurveStyle(advancedConfig) {
  const mode = String(advancedConfig?.lineInterpolation || 'linear').toLowerCase();
  switch (mode) {
    case 'spline':
    case 'smooth':
      return { stepped: false, tension: 0.35, cubicInterpolationMode: 'monotone' };
    case 'step-before':
    case 'step-start':
      return { stepped: 'before', tension: 0 };
    case 'step-middle':
    case 'step-mid':
      return { stepped: 'middle', tension: 0 };
    case 'step-after':
    case 'step-end':
      return { stepped: 'after', tension: 0 };
    case 'linear':
    default:
      return { stepped: false, tension: 0 };
  }
}

/** Estilo Chart.js para datasets de línea (huecos, curva). */
function lineDatasetExtras(type, fillMissingAsZero, advancedConfig) {
  if (type === 'radar') return { fill: true };
  if (type !== 'line') return {};
  return {
    fill: false,
    spanGaps: !fillMissingAsZero,
    ...resolveLineCurveStyle(advancedConfig),
  };
}

function seriesPointValueForAxis(groupedMap, xValue, { fillMissingAsZero, chartType }) {
  if (!Object.prototype.hasOwnProperty.call(groupedMap, xValue)) {
    if (chartType === 'bar' || chartType === 'radar') return 0;
    return fillMissingAsZero ? 0 : null;
  }
  return groupedMap[xValue];
}

function isPieArcVisible(chart, datasetIndex, index) {
  const meta = chart.getDatasetMeta(datasetIndex);
  if (!meta || meta.hidden) return false;
  const arc = meta.data?.[index];
  if (!arc || arc.hidden || arc.skip) return false;
  if (typeof chart.getDataVisibility === 'function') {
    return chart.getDataVisibility(index);
  }
  return true;
}

function visiblePieTotal(chart, dataset, datasetIndex = 0) {
  return (dataset?.data || []).reduce((acc, v, i) => {
    if (!isPieArcVisible(chart, datasetIndex, i)) return acc;
    return acc + (Number(v) || 0);
  }, 0);
}

function getPieSliceCategoryLabel(chart, dataset, index) {
  const fromDataset = dataset?.categoryLabels?.[index];
  if (fromDataset != null && String(fromDataset).trim() !== '') return String(fromDataset);
  const fromChart = chart.data?.labels?.[index];
  if (fromChart != null && String(fromChart).trim() !== '') return String(fromChart);
  const legendText = chart.legend?.legendItems?.[index]?.text;
  if (legendText != null && String(legendText).trim() !== '') return String(legendText);
  return '';
}

function mergePieLabelPluginOpts(chart) {
  const raw = chart.options?.plugins?.pieDoughnutValueLabels || {};
  return {
    showValue: raw.showValue !== false,
    showPercent: raw.showPercent !== false,
    showName: Boolean(raw.showName),
    labelPosition: raw.labelPosition || 'inside',
    minChartLabelPercent: resolvePieMinChartLabelPercent(raw.minChartLabelPercent),
    percentDef: raw.percentDef,
    minChartLabelAngleDeg: Number(raw.minChartLabelAngleDeg) || DEFAULT_PIE_MIN_CHART_LABEL_ANGLE_DEG,
    color: raw.color,
    font: raw.font,
    lineColor: raw.lineColor,
  };
}

function shouldDrawPieChartSliceLabel(value, total, element, opts) {
  if (total <= 0 || value <= 0) return false;
  const pct = (100 * value) / total;
  if (pct < opts.minChartLabelPercent) return false;
  const angleDeg = ((element.endAngle - element.startAngle) * 180) / Math.PI;
  if (angleDeg < opts.minChartLabelAngleDeg) return false;
  return true;
}

/** Etiquetas en el gráfico cuando también hay leyenda: solo métricas cortas. */
function pieChartOverlayLabelOpts(opts) {
  if (opts.labelPosition !== 'legendAndChart') return opts;
  if (opts.showPercent) {
    return { ...opts, showName: false, showValue: false, showPercent: true };
  }
  if (opts.showValue) {
    return { ...opts, showName: false, showValue: true, showPercent: false };
  }
  return { ...opts, showName: false, showValue: false, showPercent: false };
}

function buildPieLabelText(value, total, opts, categoryName = '') {
  const pct = total > 0 ? (100 * value) / total : 0;
  const valueStr = opts.percentDef
    ? formatCalculatedDisplay(value, opts.percentDef)
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const pctStr = `${pct.toFixed(1)}%`;
  const name = opts.showName && categoryName ? String(categoryName).trim() : '';
  const showValue = opts.showValue;
  const showPercent = opts.showPercent;

  if (!name && !showValue && !showPercent) return '';

  const metricParts = [];
  if (showValue) metricParts.push(valueStr);
  if (showPercent) metricParts.push(pctStr);

  if (!name) {
    if (metricParts.length === 2) return `${metricParts[0]} (${metricParts[1]})`;
    return metricParts[0] || '';
  }
  if (metricParts.length === 0) return name;
  if (metricParts.length === 2) return `${name}: ${metricParts[0]} (${metricParts[1]})`;
  return `${name}: ${metricParts[0]}`;
}

function generatePieLegendLabels(chart) {
  const dataset = chart.data.datasets[0];
  const meta = chart.getDatasetMeta(0);
  if (!dataset || !meta?.controller) {
    return ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
  }
  const pluginOpts = mergePieLabelPluginOpts(chart);
  const labels = chart.data.labels || [];
  const chartWidth = Number(chart.width) || 0;
  const compact = isCompactPieLegendViewport(chartWidth);

  return labels.map((label, i) => {
    const style = meta.controller.getStyle(i);
    const value = Number(dataset.data[i]) || 0;
    const total = visiblePieTotal(chart, dataset, 0);
    const metrics = buildPieLabelText(value, total, {
      showName: false,
      showValue: compact ? false : pluginOpts.showValue,
      showPercent: pluginOpts.showPercent,
    });
    const base = label != null ? String(label) : '';
    const text = metrics ? `${base} — ${metrics}` : base;
    return {
      text,
      fillStyle: style.backgroundColor,
      strokeStyle: style.borderColor,
      lineWidth: style.borderWidth,
      hidden: !isPieArcVisible(chart, 0, i),
      index: i,
      datasetIndex: 0,
    };
  });
}

function stackPieCalloutLabels(items, minGap, centerY) {
  if (!items.length) return;
  if (items.length === 1) {
    items[0].labelY = centerY;
    return;
  }
  const blockHeight = (items.length - 1) * minGap;
  const topY = centerY - blockHeight / 2;
  items.forEach((item, i) => {
    item.labelY = topY + i * minGap;
  });
}

function drawPieInsideLabel(ctx, element, midAngle, text, opts) {
  const radius = (element.innerRadius + element.outerRadius) / 2;
  const x = element.x + Math.cos(midAngle) * radius;
  const y = element.y + Math.sin(midAngle) * radius;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = opts.color ?? '#333';
  ctx.font = opts.font ?? '12px sans-serif';
  ctx.fillText(text, x, y);
}

function drawPieOutsideCallouts(ctx, chart, meta, dataset, opts, chartArea) {
  const font = opts.font ?? '600 13px sans-serif';
  ctx.font = font;
  const minGap = 22;
  const radialLead = 10;
  const labelPad = 8;
  const total = visiblePieTotal(chart, dataset, 0);
  if (total === 0) return;
  const firstArc = meta.data.find((element, index) => element && isPieArcVisible(chart, 0, index));
  const cx = firstArc?.x ?? (chartArea.left + chartArea.right) / 2;
  const cy = firstArc?.y ?? (chartArea.top + chartArea.bottom) / 2;
  const outerR =
    firstArc?.outerRadius ??
    Math.min(chartArea.right - chartArea.left, chartArea.bottom - chartArea.top) * 0.38;
  const connectorX = cx + outerR + radialLead;
  const callouts = [];

  meta.data.forEach((element, index) => {
    if (!isPieArcVisible(chart, 0, index)) return;
    const value = Number(dataset.data[index]) || 0;
    if (value === 0) return;
    const categoryName = getPieSliceCategoryLabel(chart, dataset, index);
    const text = buildPieLabelText(value, total, opts, categoryName);
    if (!text) return;

    const midAngle = (element.startAngle + element.endAngle) / 2;
    const cos = Math.cos(midAngle);
    const sin = Math.sin(midAngle);
    const edgeX = element.x + cos * element.outerRadius;
    const edgeY = element.y + sin * element.outerRadius;

    callouts.push({
      edgeX,
      edgeY,
      startAngle: element.startAngle,
      text,
      lineColor: element.options?.borderColor || opts.lineColor || '#888',
    });
  });

  if (!callouts.length) return;

  // Bloque único a la derecha: orden horario del pastel, apilado y centrado verticalmente.
  callouts.sort((a, b) => a.startAngle - b.startAngle);
  stackPieCalloutLabels(callouts, minGap, cy);

  const labelX = connectorX + labelPad;

  callouts.forEach((item) => {
    ctx.save();
    ctx.strokeStyle = item.lineColor;
    ctx.lineWidth = 1.25;
    ctx.beginPath();
    ctx.moveTo(item.edgeX, item.edgeY);
    ctx.lineTo(connectorX, item.edgeY);
    ctx.lineTo(connectorX, item.labelY);
    ctx.lineTo(labelX, item.labelY);
    ctx.stroke();

    ctx.fillStyle = opts.color ?? '#222';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = font;
    ctx.fillText(item.text, labelX, item.labelY);
    ctx.restore();
  });
}

// Plugin: etiquetas de valor/porcentaje en pastel/dona (dentro, callouts o leyenda)
const pieDoughnutValueLabelsPlugin = {
  id: 'pieDoughnutValueLabels',
  afterDatasetsDraw(chart) {
    const type = chart.config.type;
    if (type !== 'pie' && type !== 'doughnut') return;

    const opts = mergePieLabelPluginOpts(chart);
    const drawMode = pieChartDrawMode(opts.labelPosition);
    if (drawMode === 'none') return;
    if (!opts.showValue && !opts.showPercent && !opts.showName) return;

    const ctx = chart.ctx;
    const dataset = chart.data.datasets[0];
    if (!dataset?.data?.length) return;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;

    const chartArea = chart.chartArea;
    if (!chartArea) return;

    if (drawMode === 'outside') {
      drawPieOutsideCallouts(ctx, chart, meta, dataset, opts, chartArea);
      return;
    }

    const total = visiblePieTotal(chart, dataset, 0);
    if (total === 0) return;
    const overlayOpts = pieChartOverlayLabelOpts(opts);

    meta.data.forEach((element, index) => {
      if (!isPieArcVisible(chart, 0, index)) return;
      const value = Number(dataset.data[index]) || 0;
      if (value === 0) return;
      if (
        opts.labelPosition === 'legendAndChart' &&
        !shouldDrawPieChartSliceLabel(value, total, element, opts)
      ) {
        return;
      }
      const categoryName = getPieSliceCategoryLabel(chart, dataset, index);
      const text = buildPieLabelText(value, total, overlayOpts, categoryName);
      if (!text) return;
      const midAngle = (element.startAngle + element.endAngle) / 2;
      drawPieInsideLabel(ctx, element, midAngle, text, opts);
    });
  },
};

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController,
  PieController,
  DoughnutController,
  ScatterController,
  RadarController,
  barValueLabelsPlugin,
  lineValueLabelsPlugin,
  pieDoughnutValueLabelsPlugin,
  zoomPlugin
);

const KPI_FONT_SIZE_OPTIONS = new Set(['small', 'medium', 'large']);

function normalizeKpiFontSizeOption(value) {
  return KPI_FONT_SIZE_OPTIONS.has(value) ? value : null;
}

/** Tamaño responsivo (xs/sm) del valor KPI segun la opcion elegida. */
function resolveKpiDisplayFontSize(fontOption, display, advancedConfig) {
  const explicit = normalizeKpiFontSizeOption(fontOption);
  const autoCompact =
    !explicit &&
    typeof display === 'string' &&
    display.length > 12 &&
    !advancedConfig?.yColumnIsDuration;
  if (explicit === 'small') return { xs: '0.6875rem', sm: '0.75rem' };
  if (explicit === 'medium') return { xs: '1rem', sm: '1.125rem' };
  if (explicit === 'large') return { xs: '1.75rem', sm: '1.875rem' };
  if (autoCompact) return { xs: '0.875rem', sm: '0.9375rem' };
  return { xs: '1.75rem', sm: '1.875rem' };
}

const ChartWidget = ({
  type,
  data,
  xColumn,
  yColumn,
  groupByColumn,
  labelColumn,
  filterValues,
  deltaColumn,
  percentColumn,
  occupancyColumn,
  cumulativeColumn,
  hourCycleStart,
  xDateBucket,
  xDateDisplayFormat,
  labelDateBucket,
  labelDateDisplayFormat,
  stacked,
  horizontal,
  advancedConfig,
  kpiValueFontSize,
  heatmapGridConfig,
  imageDensityConfig,
  imageDensityShowImageControls = false,
  imageDensityAllowPositionEdit = false,
  imageCarouselConfig,
  geoHeatmapConfig,
  geoHeatmapShowControls = false,
  intervalConfig,
  columnDurationFormats,
  mapConfig,
  label,
  getAuthHeaders,
  onMapConfigChange,
  onImageDensityConfigChange,
  onHeatmapPositionDraftChange,
  chartZoomEnabled = true,
  presentationMode = false,
  periodCaption = null,
  unsavedSessionId,
}) => {
  const { palette } = useStyleTemplate();
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const [chartContainerWidth, setChartContainerWidth] = useState(0);
  const zoomActive =
    chartZoomEnabled &&
    isChartZoomType(type) &&
    advancedConfig?.chartZoomEnabled !== false;

  useEffect(() => {
    const node = chartContainerRef.current;
    if (!node) return undefined;
    const update = () => {
      const w = node.offsetWidth || 0;
      const h = node.offsetHeight || 0;
      if (w < 8 || h < 8) return;
      setChartContainerWidth(w);
    };
    update();
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(node);
    }
    window.addEventListener('resize', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [type]);

  const handleResetZoom = () => {
    const chart = chartRef.current;
    if (chart && typeof chart.resetZoom === 'function') {
      chart.resetZoom();
    }
  };

  useEffect(() => {
    if (!zoomActive) return undefined;
    const node = chartContainerRef.current;
    if (!node) return undefined;
    let lastTapAt = 0;
    const onTouchEnd = (event) => {
      if (event.touches.length > 0) return;
      const now = Date.now();
      if (now - lastTapAt < 320) {
        handleResetZoom();
        lastTapAt = 0;
        event.preventDefault();
        return;
      }
      lastTapAt = now;
    };
    node.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => node.removeEventListener('touchend', onTouchEnd);
  }, [zoomActive]);

  const pal = palette?.colors?.length ? palette : vsaasTheme.chart;
  const seriesColors = advancedConfig?.seriesColors || {};
  const seriesColorIndex = advancedConfig?.seriesColorIndex || {};
  const colorFor = (seriesLabel, index) => {
    const idx = seriesColorIndex[seriesLabel];
    if (idx !== undefined && idx !== null && Number.isFinite(idx)) {
      const i = idx % (pal.colors?.length || 1);
      return {
        bg: (pal.colorsWithAlpha && pal.colorsWithAlpha[i]) || hexToRgba(pal.colors[i]),
        border: (pal.borderColors && pal.borderColors[i]) || pal.colors[i],
        point: pal.colors[i],
      };
    }
    const hex = seriesColors[seriesLabel];
    if (hex) return { bg: hexToRgba(hex), border: hex, point: hex };
    return {
      bg: pal.colorsWithAlpha[index % (pal.colorsWithAlpha?.length || 1)],
      border: pal.borderColors[index % (pal.borderColors?.length || 1)],
      point: pal.colors[index % (pal.colors?.length || 1)],
    };
  };

  // Helper para obtener valores de configuración avanzada
  const getPointRadius = () => {
    return advancedConfig?.pointRadius !== '' && advancedConfig?.pointRadius !== undefined 
      ? parseFloat(advancedConfig.pointRadius) 
      : 3;
  };
  
  const getBorderWidth = () => {
    if (advancedConfig?.borderWidth !== '' && advancedConfig?.borderWidth !== undefined) {
      return parseFloat(advancedConfig.borderWidth);
    }
    return type === 'bar' ? 0 : 2;
  };

  const yPercentDef = useMemo(
    () => calculatedPercentDef(advancedConfig, yColumn),
    [advancedConfig, yColumn]
  );
  const xPercentDef = useMemo(
    () => calculatedPercentDef(advancedConfig, xColumn),
    [advancedConfig, xColumn]
  );

  const chartData = useMemo(() => {
    if (!data || data.length === 0 || !xColumn || !yColumn) {
      return null;
    }
    const axisSort = { hourCycleStart, direction: 'asc', xDateBucket };
    const fillTimeAxisGaps =
      (type === 'line' || type === 'bar') &&
      advancedConfig?.fillTimeAxisGaps === true &&
      supportsFillTimeAxisGaps(xDateBucket);
    const xKeyFn =
      fillTimeAxisGaps && supportsFillTimeAxisGaps(xDateBucket)
        ? (value) => normalizeTimeAxisKey(value, xDateBucket)
        : axisCategoryKey;
    const xKeysFromRows = (rows) => rows.map((row) => xKeyFn(rowValue(row, xColumn)));
    const labelsFromKeys = (keys) => keys.map((k) => (
      xPercentDef ? formatCalculatedDisplay(k, xPercentDef) : formatCategoryLabel(k, xDateBucket, xDateDisplayFormat)
    ));
    const resolveUniqueXValues = (keys) => {
      let unique = uniqueValuesInOrder(keys, axisSort);
      if (fillTimeAxisGaps && canFillTimeAxisGaps(xDateBucket, unique)) {
        unique = expandTimeAxisKeys(unique, xDateBucket);
      }
      return unique;
    };
    const pointOpts = {
      fillMissingAsZero: type === 'line' ? advancedConfig?.lineFillMissingAsZero !== false : true,
      chartType: type,
    };
    const defaultSeriesLabel = label || yColumn;
    const activeLabelColumn =
      labelColumn && data.some((row) => hasNonEmptyCell(row, labelColumn))
        ? labelColumn
        : null;
    const fillMissingAsZero = pointOpts.fillMissingAsZero;

    try {
      // Procesar datos según el tipo de gráfico
      if (type === 'pie' || type === 'doughnut') {
        // Agrupar por xColumn y sumar yColumn
        const grouped = {};
        data.forEach((row) => {
          const key = axisCategoryKey(rowValue(row, xColumn));
          const value = parseFloat(rowValue(row, yColumn)) || 0;
          grouped[key] = (grouped[key] || 0) + value;
        });

        const labels = uniqueValuesInOrder(xKeysFromRows(data), axisSort);
        const bg = labels.map((lbl, i) => colorFor(lbl, i).bg);
        const border = labels.map((lbl, i) => colorFor(lbl, i).border);
        const displayLabels = labelsFromKeys(labels);
        return {
          labels: displayLabels,
          datasets: [
            {
              label: yColumn,
              data: labels.map((lbl) => grouped[lbl] || 0),
              backgroundColor: bg,
              borderColor: border,
              borderWidth: 2,
              categoryLabels: displayLabels,
            },
          ],
        };
      } else if (type === 'scatter') {
        // Si hay labelColumn con valores, agrupar por etiquetas
        if (activeLabelColumn) {
          const groupedByLabel = {};
          data.forEach((row) => {
            const labelValue = seriesLabelFromRow(
              row,
              activeLabelColumn,
              defaultSeriesLabel,
              labelDateBucket,
              labelDateDisplayFormat
            );
            if (!groupedByLabel[labelValue]) {
              groupedByLabel[labelValue] = [];
            }
            groupedByLabel[labelValue].push({
              x: parseFloat(rowValue(row, xColumn)) || 0,
              y: parseFloat(rowValue(row, yColumn)) || 0,
            });
          });

          const datasets = Object.keys(groupedByLabel).map((labelValue, index) => {
            const c = colorFor(labelValue, index);
            return {
              label: labelValue,
              data: groupedByLabel[labelValue],
              backgroundColor: c.bg,
              borderColor: c.border,
              borderWidth: 2,
            };
          });

          return { datasets };
        } else {
          const c = colorFor(label || yColumn, 0);
          return {
            datasets: [
              {
                label: label || yColumn,
                data: data.map((row) => ({
                  x: parseFloat(rowValue(row, xColumn)) || 0,
                  y: parseFloat(rowValue(row, yColumn)) || 0,
                })),
                backgroundColor: c.bg,
                borderColor: c.border,
                borderWidth: 2,
              },
            ],
          };
        }
      } else {
        // Line y Bar
        // Lógica: 
        // 1. groupByColumn: agrupa y suma valores Y (ej: agrupar por tiendas para sumar eventos por día)
        // 2. labelColumn: crea diferentes series/colores (ej: colorear por turno)
        // 3. Si hay ambas: primero agrupar por groupByColumn (sumar), luego colorear por labelColumn
        
        if (groupByColumn || activeLabelColumn) {
          const uniqueXValues = resolveUniqueXValues(xKeysFromRows(data));
          const displayLabels = labelsFromKeys(uniqueXValues);
          
          if (groupByColumn && activeLabelColumn) {
            const groupedByAll = {};
            data.forEach((row) => {
              const xValue = xKeyFn(rowValue(row, xColumn));
              const groupValue = String(rowValue(row, groupByColumn) ?? 'Sin grupo');
              const labelValue = seriesLabelFromRow(
                row,
                activeLabelColumn,
                defaultSeriesLabel,
                labelDateBucket,
                labelDateDisplayFormat
              );
              const yValue = parseFloat(rowValue(row, yColumn)) || 0;
              
              const key = `${xValue}|||${groupValue}|||${labelValue}`;
              groupedByAll[key] = (groupedByAll[key] || 0) + yValue;
            });
            
            const groupedByLabel = {};
            Object.keys(groupedByAll).forEach(key => {
              const [xValue, , labelValue] = key.split('|||');
              if (!groupedByLabel[labelValue]) {
                groupedByLabel[labelValue] = {};
              }
              groupedByLabel[labelValue][xValue] = (groupedByLabel[labelValue][xValue] || 0) + groupedByAll[key];
            });
            
            const datasets = Object.keys(groupedByLabel).map((labelValue, index) => {
              const values = uniqueXValues.map((xValue) =>
                seriesPointValueForAxis(groupedByLabel[labelValue], xValue, pointOpts)
              );
              const c = colorFor(labelValue, index);
              return {
                label: labelValue,
                data: values,
                borderColor: c.border,
                backgroundColor: type === 'bar' || type === 'radar' ? c.bg : undefined,
                borderWidth: getBorderWidth(),
                ...lineDatasetExtras(type, fillMissingAsZero, advancedConfig),
                pointBackgroundColor: c.point,
                pointBorderColor: '#FFFFFF',
                pointBorderWidth: 2,
                pointRadius: getPointRadius(),
              };
            });
            return { labels: displayLabels, datasets };
          } else if (activeLabelColumn) {
            const groupedByLabel = {};
            data.forEach((row) => {
              const labelValue = seriesLabelFromRow(
                row,
                activeLabelColumn,
                defaultSeriesLabel,
                labelDateBucket,
                labelDateDisplayFormat
              );
              if (!groupedByLabel[labelValue]) {
                groupedByLabel[labelValue] = {};
              }
              const xValue = xKeyFn(rowValue(row, xColumn));
              const yValue = parseFloat(rowValue(row, yColumn)) || 0;
              groupedByLabel[labelValue][xValue] = (groupedByLabel[labelValue][xValue] || 0) + yValue;
            });

            const datasets = Object.keys(groupedByLabel).map((labelValue, index) => {
              const values = uniqueXValues.map((xValue) =>
                seriesPointValueForAxis(groupedByLabel[labelValue], xValue, pointOpts)
              );
              const c = colorFor(labelValue, index);
              return {
                label: labelValue,
                data: values,
                borderColor: c.border,
                backgroundColor: type === 'bar' || type === 'radar' ? c.bg : undefined,
                borderWidth: getBorderWidth(),
                ...lineDatasetExtras(type, fillMissingAsZero, advancedConfig),
                pointBackgroundColor: c.point,
                pointBorderColor: '#FFFFFF',
                pointBorderWidth: 2,
                pointRadius: getPointRadius(),
              };
            });
            return { labels: displayLabels, datasets };
          } else if (groupByColumn) {
            const groupedByGroup = {};
            data.forEach((row) => {
              const groupValue = String(rowValue(row, groupByColumn) ?? 'Sin grupo');
              if (!groupedByGroup[groupValue]) {
                groupedByGroup[groupValue] = {};
              }
              const xValue = xKeyFn(rowValue(row, xColumn));
              const yValue = parseFloat(rowValue(row, yColumn)) || 0;
              groupedByGroup[groupValue][xValue] = (groupedByGroup[groupValue][xValue] || 0) + yValue;
            });

            const datasets = Object.keys(groupedByGroup).map((groupValue, index) => {
              const values = uniqueXValues.map((xValue) =>
                seriesPointValueForAxis(groupedByGroup[groupValue], xValue, pointOpts)
              );
              const c = colorFor(groupValue, index);
              return {
                label: groupValue,
                data: values,
                borderColor: c.border,
                backgroundColor: type === 'bar' || type === 'radar' ? c.bg : undefined,
                borderWidth: getBorderWidth(),
                ...lineDatasetExtras(type, fillMissingAsZero, advancedConfig),
                pointBackgroundColor: c.point,
                pointBorderColor: '#FFFFFF',
                pointBorderWidth: 2,
                pointRadius: getPointRadius(),
              };
            });
            return { labels: displayLabels, datasets };
          }
        } else {
          const grouped = {};
          data.forEach((row) => {
            const xValue = xKeyFn(rowValue(row, xColumn));
            grouped[xValue] = (grouped[xValue] || 0) + (parseFloat(rowValue(row, yColumn)) || 0);
          });
          const uniqueXValues = resolveUniqueXValues(Object.keys(grouped));
          const values = uniqueXValues.map((xValue) =>
            seriesPointValueForAxis(grouped, xValue, pointOpts)
          );

          const c = colorFor(yColumn, 0);
          return {
            labels: labelsFromKeys(uniqueXValues),
            datasets: [
              {
                label: defaultSeriesLabel,
                data: values,
                borderColor: type === 'line' ? c.border : undefined,
                backgroundColor: type === 'bar' || type === 'radar' ? c.bg : undefined,
                borderWidth: getBorderWidth(),
                ...lineDatasetExtras(type, fillMissingAsZero, advancedConfig),
                pointBackgroundColor: c.point,
                pointBorderColor: '#FFFFFF',
                pointBorderWidth: 2,
                pointRadius: getPointRadius(),
              },
            ],
          };
        }
      }
    } catch (error) {
      console.error('Error procesando datos del gráfico:', error);
      return null;
    }
  }, [data, xColumn, yColumn, groupByColumn, labelColumn, hourCycleStart, xDateBucket, xDateDisplayFormat, labelDateBucket, labelDateDisplayFormat, type, label, advancedConfig, palette, xPercentDef]);

  const showMetricPercentLine =
    (type === 'bar' || type === 'line') &&
    Boolean(percentColumn) &&
    advancedConfig?.showMetricPercentLine === true;

  // Linea extra con el % de variacion (diferencia/comparacion de periodo), en su propio
  // eje derecho, para no mezclar escalas con el valor principal (miles vs un digito).
  // Se arma aparte de chartData en vez de tocar esa funcion, para no arriesgar la logica
  // de agrupamiento existente: solo se agrega al final, opcional.
  const chartDataForRender = useMemo(() => {
    if (!chartData || !showMetricPercentLine) return chartData;
    const metricValues = (chartData.labels || []).map((lbl) => {
      const row = (data || []).find(
        (r) =>
          rowValue(r, percentColumn) != null &&
          resolveAxisCategoryLabel(rowValue(r, xColumn), { type, xDateBucket, xDateDisplayFormat, advancedConfig }) === lbl
      );
      return row ? Number(rowValue(row, percentColumn)) : null;
    });
    return {
      ...chartData,
      datasets: [
        ...chartData.datasets,
        {
          type: 'line',
          label: '% variación',
          data: metricValues,
          yAxisID: 'y1',
          // Rosa de la paleta "Pastel" del proyecto (DEFAULT_PALETTES.pastel en theme.js),
          // para que combine con el resto de las series en vez de un color puro llamativo.
          borderColor: '#DC8E8E',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [4, 3],
          pointRadius: 3,
          spanGaps: false,
          order: 0,
        },
      ],
    };
  }, [chartData, showMetricPercentLine, data, percentColumn, xColumn, xDateBucket, xDateDisplayFormat, type, advancedConfig]);

  const options = useMemo(() => {
    const seriesCount = chartData?.datasets?.length ?? 0;
    const isPieChart = type === 'pie' || type === 'doughnut';
    // Con columnas de diferencia/porcentaje presentes (ej. comparacion de periodo), se
    // arma un lookup de fila original por (etiqueta de X, serie) para poder agregarlas al
    // tooltip sin duplicar la logica de agrupamiento que ya arma chartData. La clave usa
    // la etiqueta ya formateada (formatCategoryLabel), la misma que Chart.js muestra en
    // el eje y devuelve en context.label; el valor crudo de la fila no sirve de clave
    // cuando el eje X es una fecha real con agrupacion (ej. "Por dia"), porque ahi la
    // etiqueta mostrada no es igual al valor crudo.
    const comparisonRowLookup =
      !isPieChart && labelColumn && (deltaColumn || percentColumn || occupancyColumn || cumulativeColumn)
        ? new Map(
            (data || []).map((row) => [
              `${resolveAxisCategoryLabel(rowValue(row, xColumn), { type, xDateBucket, xDateDisplayFormat, advancedConfig })}||${rowValue(row, labelColumn)}`,
              row,
            ])
          )
        : null;

    const tooltipCallbacks = {};
    if (isPieChart) {
      tooltipCallbacks.title = (tooltipItems) => {
        if (!tooltipItems?.length) return '';
        const { dataIndex, chart: c } = tooltipItems[0];
        return c.data.labels?.[dataIndex] ?? '';
      };
      tooltipCallbacks.label = (context) => {
        const n = Number(context.parsed);
        if (!Number.isFinite(n)) return String(context.parsed ?? '');
        if (yPercentDef) return formatCalculatedDisplay(n, yPercentDef);
        return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
      };
    } else if (yPercentDef) {
      tooltipCallbacks.label = (context) => {
        const n = context.parsed?.y ?? context.parsed;
        const formatted = formatCalculatedDisplay(n, yPercentDef);
        const prefix = context.dataset?.label ? `${context.dataset.label}: ` : '';
        return `${prefix}${formatted}`;
      };
    } else if (advancedConfig?.yColumnIsDuration) {
      tooltipCallbacks.label = (context) => {
        const n = context.parsed?.y ?? context.parsed;
        const formatted = formatDurationMinutes(n, advancedConfig.yDurationTooltipFormat || 'verbose');
        const prefix = context.dataset?.label ? `${context.dataset.label}: ` : '';
        return `${prefix}${formatted}`;
      };
    }
    if (comparisonRowLookup) {
      // Se agrega la diferencia/porcentaje de variacion despues del valor que Chart.js
      // ya muestra por defecto (o del formateado arriba, si es duracion), sin pisarlo.
      tooltipCallbacks.afterLabel = (context) => {
        const key = `${context.label}||${context.dataset?.label}`;
        const row = comparisonRowLookup.get(key);
        if (!row) return undefined;
        const lines = [];
        const delta = deltaColumn ? Number(rowValue(row, deltaColumn)) : NaN;
        if (Number.isFinite(delta)) {
          lines.push(
            `Diferencia: ${delta > 0 ? '+' : ''}${delta.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
          );
        }
        const percent = percentColumn ? Number(rowValue(row, percentColumn)) : NaN;
        if (Number.isFinite(percent)) {
          lines.push(
            `% variación: ${percent > 0 ? '+' : ''}${percent.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
          );
        }
        const occupancy = occupancyColumn ? Number(rowValue(row, occupancyColumn)) : NaN;
        if (Number.isFinite(occupancy)) {
          lines.push(
            `% sobre divisor: ${occupancy > 0 ? '+' : ''}${occupancy.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
          );
        }
        const cumulative = cumulativeColumn ? Number(rowValue(row, cumulativeColumn)) : NaN;
        if (Number.isFinite(cumulative)) {
          lines.push(`Acumulado: ${cumulative.toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
        }
        return lines.length ? lines : undefined;
      };
    }
    const barShowTotal = type === 'bar' && advancedConfig?.barShowTotalLabel === true;
    const barShowSegment = type === 'bar' && advancedConfig?.barShowSegmentLabel === true;
    const lineShowValue = type === 'line' && advancedConfig?.lineShowValueLabel === true;
    const categoryCount =
      chartData?.datasets?.[0]?.data?.length ?? chartData?.labels?.length ?? 0;
    const lineLabelMode =
      type === 'line'
        ? resolveLineValueLabelMode({
            showValue: lineShowValue,
            containerWidth: chartContainerWidth,
            categoryCount,
            datasetCount: seriesCount,
          })
        : { showValue: false };
    const lineLabelsActive = lineLabelMode.showValue;
    const barLabelMode =
      type === 'bar'
        ? resolveBarLabelMode({
            showTotal: barShowTotal,
            showSegment: barShowSegment,
            containerWidth: chartContainerWidth,
            categoryCount: chartData?.datasets?.[0]?.data?.length ?? 0,
          })
        : { showTotal: false, showSegment: false };
    const barLabelsActive = barLabelMode.showTotal || barLabelMode.showSegment;
    const yAxisMaxManual =
      advancedConfig?.yAxisMax !== '' && advancedConfig?.yAxisMax !== undefined;
    const needsLabelHeadroom = barLabelsActive || lineLabelsActive;
    const pieLegendPosition = isPieChart
      ? resolvePieLegendPositionForViewport(advancedConfig?.legendPosition || 'left', chartContainerWidth)
      : advancedConfig?.legendPosition || 'top';
    const legendVisible = isPieChart
      ? advancedConfig?.legendDisplay !== false
      : advancedConfig?.legendDisplay !== undefined
        ? advancedConfig.legendDisplay && type !== 'scatter'
        : type !== 'scatter' && seriesCount > 1;
    const pieLegendFont = isPieChart
      ? scalePieLegendFontSize(
          resolvePieLegendFontSize(advancedConfig?.pieLegendFontSize),
          chartContainerWidth
        )
      : null;
    const compactPieLegend = isPieChart && isCompactPieLegendViewport(chartContainerWidth);
    const pieLabelPosition = isPieChart
      ? normalizePieLabelPosition(advancedConfig?.pieLabelPosition)
      : 'inside';
    const pieLabelsInLegendMode = isPieChart && isPieLegendLabelMode(pieLabelPosition);
    const chartZoomOptions = buildChartZoomPluginOptions({ enabled: zoomActive, type });
    const avgCategoryLabelChars =
      chartData?.labels?.length > 0
        ? chartData.labels.reduce((sum, label) => sum + String(label ?? '').length, 0) /
          chartData.labels.length
        : 11;
    const barHorizontal = type === 'bar' && !!horizontal;
    const categoryAxisTickOpts =
      (type === 'line' || type === 'bar') && !barHorizontal
        ? resolveCategoryAxisTickOptions(chartContainerWidth, categoryCount, {
            avgLabelChars: avgCategoryLabelChars,
          })
        : null;
    const isAxisChart = type === 'line' || type === 'bar' || type === 'scatter';
    const showXAxisTitle =
      isAxisChart &&
      !!(advancedConfig?.xAxisTitle || (!advancedConfig && type === 'scatter'));

    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: barLabelsActive
            ? isCompactBarLabelViewport(chartContainerWidth)
              ? 10
              : 16
            : needsLabelHeadroom
              ? 12
              : legendVisible && !isPieChart
                ? 10
                : 6,
          right: isPieChart && pieLabelPosition === 'outside' ? (compactPieLegend ? 72 : 128) : 4,
          bottom: (() => {
            let pad = 6;
            if (isPieChart && pieLegendPosition === 'bottom' && legendVisible) {
              pad = compactPieLegend ? 6 : 4;
            } else if (categoryAxisTickOpts?.rotated) {
              pad = isCompactBarLabelViewport(chartContainerWidth) ? 26 : 22;
            }
            if (showXAxisTitle) {
              pad = Math.max(pad, categoryAxisTickOpts?.rotated ? 38 : 22);
            }
            return pad;
          })(),
          left: isPieChart && legendVisible && pieLegendPosition === 'left' ? 2 : 4,
        },
      },
      // Reducir duración de animación para que barras/líneas no se sientan en cámara lenta (Chart.js default 1000ms)
      ...(type === 'bar' || type === 'line' ? { animations: { duration: 350 } } : {}),
      ...(barHorizontal && { indexAxis: 'y' }),
      plugins: {
        legend: {
          display: legendVisible,
          position: pieLegendPosition,
          align: isPieChart ? (pieLegendPosition === 'bottom' ? 'center' : 'start') : 'center',
          padding: isPieChart ? (compactPieLegend ? 4 : 2) : 4,
          labels: {
            padding: isPieChart ? (compactPieLegend ? 8 : 5) : 8,
            boxWidth: isPieChart ? pieLegendFont.box : 12,
            boxHeight: isPieChart ? pieLegendFont.box : 12,
            font: {
              size: isPieChart ? pieLegendFont.px : 12,
            },
            ...(isPieChart && pieLabelsInLegendMode ? { generateLabels: generatePieLegendLabels } : {}),
          },
        },
        tooltip: {
          enabled: true,
          ...(Object.keys(tooltipCallbacks).length > 0 ? { callbacks: tooltipCallbacks } : {}),
        },
        ...(type === 'bar' && {
          barValueLabels: {
            showTotal: advancedConfig?.barShowTotalLabel === true,
            showSegment: advancedConfig?.barShowSegmentLabel === true,
            containerWidth: chartContainerWidth,
            decimalPlaces: advancedConfig?.valueDecimalPlaces,
            yColumnIsDuration: advancedConfig?.yColumnIsDuration,
            durationFormat: advancedConfig?.yDurationFormat || 'auto',
            percentDef: yPercentDef || undefined,
          },
        }),
        ...(type === 'line' && {
          lineValueLabels: {
            showValue: advancedConfig?.lineShowValueLabel === true,
            containerWidth: chartContainerWidth,
            decimalPlaces: advancedConfig?.valueDecimalPlaces,
            yColumnIsDuration: advancedConfig?.yColumnIsDuration,
            durationFormat: advancedConfig?.yDurationFormat || 'auto',
            percentDef: yPercentDef || undefined,
          },
        }),
        ...((type === 'pie' || type === 'doughnut') && {
          pieDoughnutValueLabels: {
            showValue: advancedConfig?.pieShowValueLabel !== false,
            showPercent: advancedConfig?.pieShowPercentLabel !== false,
            showName: advancedConfig?.pieShowNameLabel === true,
            labelPosition: pieLabelPosition,
            minChartLabelPercent: advancedConfig?.pieMinChartLabelPercent,
            percentDef: yPercentDef || undefined,
          },
        }),
        ...(chartZoomOptions ? { zoom: chartZoomOptions } : {}),
      },
    };

    // Construir configuración de escalas
    const scalesConfig = {};

    if (isAxisChart) {
      const categoryTickVisible = categoryAxisTickOpts?.visibleIndices ?? null;
      const { visibleIndices: _omit, rotated: _rotated, ...categoryTickChartOpts } =
        categoryAxisTickOpts || {};
      // En barras horizontales, Chart.js sigue llamando "x"/"y" a los ejes por su posición
      // en pantalla, no por su rol: con indexAxis:'y' la categoría pasa al eje vertical (y) y
      // el valor al horizontal (x). Estas claves apuntan cada config al eje físico correcto.
      const categoryAxisKey = barHorizontal ? 'y' : 'x';
      const valueAxisKey = barHorizontal ? 'x' : 'y';

      scalesConfig[categoryAxisKey] = {
        ...(type === 'scatter' && { type: 'linear', position: 'bottom' }),
        title: {
          display: !!(advancedConfig?.xAxisTitle || (!advancedConfig && type === 'scatter')),
          text: advancedConfig?.xAxisTitle || xColumn || '',
        },
        grid: {
          display: advancedConfig?.gridLines !== false,
        },
        ...(categoryAxisTickOpts && {
          ticks: {
            ...categoryTickChartOpts,
            ...(categoryTickVisible && {
              autoSkip: false,
              callback(value, index) {
                if (!categoryTickVisible.has(index)) return '';
                return this.getLabelForValue(value);
              },
            }),
          },
        }),
        ...(advancedConfig?.xAxisMin !== '' && advancedConfig?.xAxisMin !== undefined && { min: parseFloat(advancedConfig.xAxisMin) }),
        ...(advancedConfig?.xAxisMax !== '' && advancedConfig?.xAxisMax !== undefined && { max: parseFloat(advancedConfig.xAxisMax) }),
        ...(type === 'bar' && stacked && { stacked: true }),
      };

      scalesConfig[valueAxisKey] = {
        title: {
          display: !!(advancedConfig?.yAxisTitle || (!advancedConfig && type === 'scatter')),
          text: advancedConfig?.yAxisTitle || yColumn || '',
        },
        grid: {
          display: advancedConfig?.gridLines !== false,
        },
        ...(advancedConfig?.yColumnIsDuration && {
          ticks: {
            callback: (value) =>
              formatDurationMinutes(value, advancedConfig.yDurationFormat || 'auto'),
          },
        }),
        ...(yPercentDef && !advancedConfig?.yColumnIsDuration && {
          ticks: {
            callback: (value) => formatCalculatedDisplay(value, yPercentDef),
          },
        }),
        ...(advancedConfig?.yAxisMin !== '' && advancedConfig?.yAxisMin !== undefined && { min: parseFloat(advancedConfig.yAxisMin) }),
        ...(advancedConfig?.yAxisMax !== '' && advancedConfig?.yAxisMax !== undefined && { max: parseFloat(advancedConfig.yAxisMax) }),
        ...(type === 'bar' && stacked && { stacked: true }),
        ...(type === 'bar' && !yAxisMaxManual && barLabelsActive && {
          afterDataLimits: (axis) => {
            applyYAxisTopHeadroomOnly(
              axis,
              isCompactBarLabelViewport(chartContainerWidth) ? 0.14 : 0.12
            );
          },
        }),
        ...(type === 'line' && !yAxisMaxManual && lineLabelsActive && {
          afterDataLimits: (axis) => {
            applyYAxisTopHeadroomOnly(
              axis,
              isCompactBarLabelViewport(chartContainerWidth) ? 0.08 : 0.1
            );
          },
        }),
      };

      if (showMetricPercentLine) {
        scalesConfig.y1 = {
          position: 'right',
          grid: { drawOnChartArea: false },
          title: { display: true, text: '% variación' },
          ticks: { callback: (v) => `${v}%` },
        };
      }
    }

    return {
      ...baseOptions,
      ...(Object.keys(scalesConfig).length > 0 && { scales: scalesConfig }),
    };
  }, [type, xColumn, yColumn, labelColumn, deltaColumn, percentColumn, occupancyColumn, cumulativeColumn, showMetricPercentLine, data, xDateBucket, xDateDisplayFormat, stacked, horizontal, advancedConfig, chartData, chartContainerWidth, zoomActive, yPercentDef]);

  if (type === 'heatmapGrid') {
    return (
      <Box sx={{ height: '100%', minHeight: 200, flex: 1, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <HeatmapGridWidget data={data} heatmapGridConfig={heatmapGridConfig} getAuthHeaders={getAuthHeaders} />
      </Box>
    );
  }

  if (type === 'imageDensity') {
    const cfg =
      imageDensityConfig ||
      advancedConfig?.imageDensityConfig || {
        xColumn: xColumn || 'x',
        yColumn: yColumn || 'y',
        backgroundImageUrl: '',
      };
    return (
      <Box sx={{ height: '100%', minHeight: 200, flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <ImageDensityWidget
          data={data}
          imageDensityConfig={cfg}
          columnDurationFormats={
            columnDurationFormats ?? advancedConfig?.tableColumnDurationFormats
          }
          showImageControls={Boolean(imageDensityShowImageControls)}
          allowPositionEdit={Boolean(imageDensityAllowPositionEdit)}
          getAuthHeaders={getAuthHeaders}
          onConfigChange={onImageDensityConfigChange}
          onPositionDraftChange={onHeatmapPositionDraftChange}
          unsavedSessionId={unsavedSessionId}
        />
      </Box>
    );
  }

  if (type === 'imageCarousel') {
    return (
      <Box sx={{ height: '100%', minHeight: 200, flex: 1, minWidth: 0, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <ImageCarouselWidget data={data} imageCarouselConfig={imageCarouselConfig} getAuthHeaders={getAuthHeaders} />
      </Box>
    );
  }

  if (type === 'geoHeatmap') {
    const cfg =
      geoHeatmapConfig ||
      advancedConfig?.geoHeatmapConfig || {
        latColumn: xColumn || 'lat',
        lngColumn: yColumn || 'lng',
      };
    return (
      <Box sx={{ height: '100%', minHeight: 200, flex: 1, minWidth: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <GeoHeatmapWidget
          data={data}
          geoHeatmapConfig={cfg}
          getAuthHeaders={getAuthHeaders}
          showControls={Boolean(geoHeatmapShowControls)}
        />
      </Box>
    );
  }

  if (type === 'intervals') {
    return (
      <Box sx={{ height: '100%', minHeight: 200, flex: 1, minWidth: 0, position: 'relative' }}>
        <IntervalTimelineChart
          data={data}
          intervalConfig={
            intervalConfig ||
            advancedConfig?.intervalConfig || {
              startColumn: xColumn,
              endColumn: yColumn,
              seriesColumn: labelColumn,
            }
          }
          colorFor={colorFor}
          zoomEnabled={chartZoomEnabled && advancedConfig?.chartZoomEnabled !== false}
          legendDisplay={advancedConfig?.legendDisplay !== false}
        />
      </Box>
    );
  }

  if (type === 'map') {
    return (
      <Box sx={{ height: '100%', minHeight: 200, position: 'relative' }}>
        <MapWidget data={data} mapConfig={mapConfig} getAuthHeaders={getAuthHeaders} onMapConfigChange={onMapConfigChange} unsavedSessionId={unsavedSessionId} />
      </Box>
    );
  }

  if (type === 'narrative') {
    // buildNarrativePlaceholders lee row[xAlias]/row[yAlias] directo (es un util puro, sin el
    // fallback de alias con sufijo de tabla que sí tiene rowValue). Se normaliza acá antes de
    // pasarle las filas, igual que para cualquier otro tipo de gráfico.
    const narrativeRows = Array.isArray(data)
      ? data.map((row) => ({ ...row, [xColumn]: rowValue(row, xColumn), [yColumn]: rowValue(row, yColumn) }))
      : data;
    const placeholders = buildNarrativePlaceholders(narrativeRows, xColumn, yColumn);
    // Cualquier filtro activo del gráfico (fijo o con selección dinámica) queda disponible como
    // marcador por su propio nombre de columna (ej. {{sitename}}, {{category}}), para que el
    // mismo gráfico se pueda duplicar cambiando el filtro sin reescribir el texto a mano.
    const placeholderKeysInUse = new Set(placeholders.map((p) => p.key));
    Object.entries(filterValues || {}).forEach(([column, value]) => {
      if (value == null || value === '' || isInternalIdColumn(column)) return;
      const key = dedupePlaceholderKey(slugifyPlaceholderKey(column), placeholderKeysInUse);
      placeholders.push({ key, label: column, valueText: String(value) });
    });
    const segments = renderNarrativeSegments(advancedConfig?.template, placeholders);
    return (
      <Box sx={{ height: '100%', minHeight: 80, p: 1.5, overflow: 'auto' }}>
        {segments.length ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {segments.map((seg, i) =>
              seg.type === 'value' ? (
                <Box key={i} component="strong" sx={{ color: 'primary.main', fontWeight: 700 }}>
                  {seg.value}
                </Box>
              ) : (
                <React.Fragment key={i}>{seg.value}</React.Fragment>
              )
            )}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Configura el texto en el editor del gráfico.
          </Typography>
        )}
      </Box>
    );
  }

  if (type === 'kpi') {
    const row = data?.[0];
    const val = row && yColumn != null ? rowValue(row, yColumn) : null;
    const formatKpiValue = (v) => {
      if (v === null || v === undefined || v === '') return '—';
      if (yPercentDef) return formatCalculatedDisplay(v, yPercentDef);
      if (advancedConfig?.yColumnIsDuration) {
        return formatDurationMinutes(v, advancedConfig.yDurationFormat || 'auto');
      }
      if (typeof v === 'number') {
        return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
      }
      if (typeof v === 'string' || v instanceof Date) {
        const parsed = parseTemporalValue(v);
        if (parsed) {
          // Min/max sobre una columna de fecha/hora (ej. "Ultima lectura") llega como
          // string crudo; formatearlo dia-mes-año en vez de mostrar el ISO tal cual.
          const dd = String(parsed.getUTCDate()).padStart(2, '0');
          const mm = String(parsed.getUTCMonth() + 1).padStart(2, '0');
          const hh = String(parsed.getUTCHours()).padStart(2, '0');
          const mi = String(parsed.getUTCMinutes()).padStart(2, '0');
          const ss = String(parsed.getUTCSeconds()).padStart(2, '0');
          return `${dd}-${mm}-${parsed.getUTCFullYear()} ${hh}:${mi}:${ss}`;
        }
      }
      return String(v);
    };
    const display = formatKpiValue(val);
    const kpiFontOption =
      normalizeKpiFontSizeOption(kpiValueFontSize) ||
      normalizeKpiFontSizeOption(advancedConfig?.kpiValueFontSize);
    const displayFontSize = resolveKpiDisplayFontSize(kpiFontOption, display, advancedConfig);
    const thresholdColor = resolveThresholdTextColor(val, advancedConfig?.kpiColorThresholds);
    const thresholdLabel = resolveThresholdLabel(val, advancedConfig?.kpiColorThresholds);
    const comparison = advancedConfig?.kpiComparison;
    // kpiComparison es null cuando no hay ningun filtro de fecha para anclar la
    // comparacion (no aplica). Un objeto con priorValue/percentChange en null si
    // ocurre (el filtro de fecha se detecto y se corrio la consulta, pero el periodo
    // anterior no tiene datos): igual se muestra el bloque con "-", para confirmar
    // que la comparacion esta activa en vez de que parezca que no se configuro nada.
    const hasComparison = comparison != null;
    const percentChange = comparison?.percentChange;
    const trendColor =
      percentChange == null || percentChange === 0
        ? 'text.secondary'
        : percentChange > 0
          ? 'success.main'
          : 'error.main';
    const trendArrow = percentChange == null || percentChange === 0 ? '' : percentChange > 0 ? '↑ ' : '↓ ';
    const percentText =
      percentChange == null
        ? '—'
        : `${percentChange > 0 ? '+' : ''}${percentChange.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: presentationMode ? 0.25 : 0.25,
          py: presentationMode ? 0.25 : 0,
          px: presentationMode ? 1 : 1,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Fuera de modo presentación el título ya lo muestra el header del tile (ChartGrid);
            mostrarlo aquí también lo duplicaría. En presentación ese header se oculta para
            los KPI, así que este caption pasa a ser la única fuente del título. */}
        {presentationMode && (advancedConfig?.title || label) && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}
          >
            {advancedConfig?.title || label}
          </Typography>
        )}
        {periodCaption?.text && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.35,
              color: 'text.secondary',
            }}
            title={periodCaption.title || periodCaption.text}
          >
            <EventOutlinedIcon sx={{ fontSize: '0.875rem', opacity: 0.85 }} aria-hidden />
            <Typography variant="caption" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              {periodCaption.text}
            </Typography>
          </Box>
        )}
        <Typography
          variant="inherit"
          component="div"
          fontWeight={700}
          sx={{
            lineHeight: 1.1,
            textAlign: 'center',
            fontSize: displayFontSize,
            ...(thresholdColor ? { color: thresholdColor } : {}),
          }}
        >
          {display}
        </Typography>
        {thresholdLabel && (
          <Typography variant="body2" fontWeight={600} sx={thresholdColor ? { color: thresholdColor } : undefined}>
            {thresholdLabel}
          </Typography>
        )}
        {hasComparison && (
          <Box sx={{ mt: 0.5, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Año Anterior: {formatKpiValue(comparison.priorValue)}
            </Typography>
            <Typography variant="caption" fontWeight={600} color={trendColor} display="block">
              {trendArrow}% Var. Año Anterior: {percentText}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  if (!chartData) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="text.secondary">
          No hay datos para mostrar
        </Typography>
      </Box>
    );
  }

  // Mapear tipos a los nombres de tipo de Chart.js
  const chartType =
    type === 'line' ? 'line' :
    type === 'bar' ? 'bar' :
    type === 'pie' ? 'pie' :
    type === 'doughnut' ? 'doughnut' :
    type === 'scatter' ? 'scatter' :
    type === 'radar' ? 'radar' : 'line';

  return (
    <Box
      ref={chartContainerRef}
      sx={{
        height: '100%',
        width: '100%',
        minHeight: 0,
        minWidth: 0,
        flex: 1,
        position: 'relative',
        touchAction: zoomActive ? 'pan-y pinch-zoom' : undefined,
      }}
    >
      {zoomActive && (
        <MuiTooltip title="Restablecer zoom (doble toque en móvil)">
          <IconButton
            size="small"
            aria-label="Restablecer zoom"
            onClick={handleResetZoom}
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              zIndex: 2,
              bgcolor: 'background.paper',
              boxShadow: 1,
              opacity: 0.88,
              '&:hover': { opacity: 1 },
            }}
          >
            <ZoomOutMapIcon fontSize="small" />
          </IconButton>
        </MuiTooltip>
      )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
        }}
      >
        <Chart
          ref={chartRef}
          // Cambiar indexAxis (vertical/horizontal) en un chart.js ya montado puede dejar
          // caches internas de escalas/controladores desincronizadas; forzar remount evita
          // ese estado intermedio en vez de depender de que chart.update() lo resuelva solo.
          key={chartType === 'bar' ? `bar-${horizontal ? 'h' : 'v'}` : chartType}
          type={chartType}
          data={chartDataForRender}
          options={options}
          style={{ width: '100%', height: '100%' }}
        />
      </Box>
    </Box>
  );
};

export default ChartWidget;
